import { Box, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { PartnerTimeZoneTooltip } from 'src/components/DateTime';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  bubbleLabelSx,
  copyButtonSx,
  getBubbleBodySx,
  getBubbleWrapperSx,
  getMessageMetaWrapperSx,
  messageMetaCaptionSx,
  messageMetaDateSx,
} from './chat-message.styles';

export const Bubble = ({
  children,
  align,
  label,
  onCopy,
}: {
  children: ReactNode;
  align: 'left' | 'right';
  label?: string;
  onCopy?: () => void;
}) => (
  <Box className="sidekick-msg" sx={getBubbleWrapperSx(align)}>
    {label ? (
      <Typography variant="caption" sx={bubbleLabelSx}>
        {label}
      </Typography>
    ) : null}
    <Box className="bubble" sx={getBubbleBodySx(align)}>
      {children}
    </Box>
    {onCopy ? (
      <IconButton
        aria-label="Copy message"
        size="small"
        onClick={onCopy}
        className="sidekick-copy"
        sx={copyButtonSx}
      >
        <DynamicIcon name="content_copy" sx={{ fontSize: '0.75rem' }} />
      </IconButton>
    ) : null}
  </Box>
);

export const MessageWithMeta = ({
  align,
  timestamp,
  partnerId,
  children,
  label,
}: {
  align: 'left' | 'right';
  timestamp?: string;
  partnerId?: number | null;
  children: ReactNode;
  label: string;
}) => {
  const formatted = timestamp ? dayjs(timestamp).format('MMM D, h:mm A') : '';

  return (
    <Box sx={getMessageMetaWrapperSx(align)}>
      <Stack
        direction={align === 'right' ? 'row-reverse' : 'row'}
        spacing={0.5}
        alignItems="center"
        mb={0.25}
      >
        <Typography variant="caption" sx={messageMetaCaptionSx}>
          {label}
        </Typography>
        {timestamp ? (
          <PartnerTimeZoneTooltip date={timestamp} partnerId={partnerId}>
            <Typography variant="caption" className="sidekick-date" sx={messageMetaDateSx}>
              {formatted}
            </Typography>
          </PartnerTimeZoneTooltip>
        ) : null}
      </Stack>

      {children}
    </Box>
  );
};
